import { Difficulty } from '../engine';
import { el, qs, qsa } from './dom';
import { ICON_PLAY, logoMarkHtml } from './icons';

export type DiscColor = 'red' | 'yellow';

export interface HomeConfig {
  difficulty: Difficulty;
  color: DiscColor;
}

export interface HomeHandlers {
  onStart: (config: HomeConfig) => void;
}

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard'];

/** The start screen: pick difficulty + colour, then play. Owns only its own selection. */
export class HomeView {
  readonly root: HTMLElement;
  private difficulty: Difficulty = 'medium';
  private color: DiscColor = 'red';

  constructor(parent: HTMLElement, handlers: HomeHandlers) {
    this.root = el(`
      <section class="screen screen--home" aria-label="Start screen">
        <div class="card home-card">
          <div class="home-grid">
            <div class="home-intro">
              <div class="brand">
                ${logoMarkHtml('lg')}
                <span class="brand__name">Connect Four</span>
              </div>

              <p class="home-tagline">Drop your discs, line up four in a row, and outsmart the bot.</p>

              <div class="field">
                <span class="field__label">Difficulty</span>
                <div class="toggle" role="group" aria-label="Difficulty">
                  ${DIFFICULTIES.map(
                    (d) =>
                      `<button type="button" class="toggle__btn" data-difficulty="${d}">${cap(d)}</button>`,
                  ).join('')}
                </div>
              </div>

              <div class="field">
                <span class="field__label">Your color</span>
                <div class="swatches" role="group" aria-label="Your color">
                  <button type="button" class="swatch swatch--red" data-color="red" aria-label="Play as red (red moves first)"></button>
                  <button type="button" class="swatch swatch--yellow" data-color="yellow" aria-label="Play as yellow (red moves first)"></button>
                </div>
              </div>

              <button type="button" class="btn btn--primary home-play">
                ${ICON_PLAY}
                Play vs computer
              </button>

              <button type="button" class="link home-how">How to play</button>
            </div>

            <div class="home-art" aria-hidden="true">
              <div class="home-art__halo"></div>
              <span class="home-art__chip home-art__chip--blue"></span>
              <span class="home-art__chip home-art__chip--yellow"></span>
              <span class="home-art__chip home-art__chip--red"></span>
              <div class="home-art__discs">
                <div class="art-disc art-disc--red"></div>
                <div class="art-disc art-disc--yellow"></div>
                <div class="art-disc art-disc--yellow"></div>
                <div class="art-disc art-disc--red"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-overlay home-help" hidden>
          <div class="modal modal--help" role="dialog" aria-modal="true" aria-label="How to play">
            <h2 class="modal__title">How to play</h2>
            <ul class="help-list">
              <li>Click a column (or focus it and press Enter) to drop your disc.</li>
              <li>Discs fall to the lowest empty slot — just like the real game.</li>
              <li>Connect <strong>four in a row</strong> — across, down, or diagonally — to win.</li>
              <li><strong>Red always moves first.</strong> Pick yellow to let the bot open.</li>
              <li>Use <strong>Undo</strong> to take back a move and <strong>Restart</strong> for a fresh board.</li>
            </ul>
            <button type="button" class="btn btn--ghost home-help-close">Got it</button>
          </div>
        </div>
      </section>
    `);

    parent.appendChild(this.root);
    this.wire(handlers);
    this.syncSelection();
  }

  private wire(handlers: HomeHandlers): void {
    qsa<HTMLButtonElement>(this.root, '.toggle__btn').forEach((btn) =>
      btn.addEventListener('click', () => {
        this.difficulty = btn.dataset.difficulty as Difficulty;
        this.syncSelection();
      }),
    );

    qsa<HTMLButtonElement>(this.root, '.swatch').forEach((btn) =>
      btn.addEventListener('click', () => {
        this.color = btn.dataset.color as DiscColor;
        this.syncSelection();
      }),
    );

    qs<HTMLButtonElement>(this.root, '.home-play').addEventListener('click', () =>
      handlers.onStart({ difficulty: this.difficulty, color: this.color }),
    );

    const help = qs(this.root, '.home-help');
    qs<HTMLButtonElement>(this.root, '.home-how').addEventListener('click', () => {
      help.hidden = false;
    });
    qs<HTMLButtonElement>(this.root, '.home-help-close').addEventListener('click', () => {
      help.hidden = true;
    });
    help.addEventListener('click', (e) => {
      if (e.target === help) help.hidden = true;
    });
  }

  /** Reflect the current selection in the toggle buttons and colour swatches. */
  private syncSelection(): void {
    qsa<HTMLButtonElement>(this.root, '.toggle__btn').forEach((btn) => {
      const active = btn.dataset.difficulty === this.difficulty;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
    qsa<HTMLButtonElement>(this.root, '.swatch').forEach((btn) => {
      const active = btn.dataset.color === this.color;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  show(): void {
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
  }
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
